import InfoOutlineIcon from "@mui/icons-material/InfoOutlined";
import PasswordIcon from "@mui/icons-material/Password";
import {
  Box,
  Button,
  Card,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "../App.css";
import logo from "../assets/Dark_L&W.png";
import ErrorsDisplay from "../components/ErrorsDisplay";
import LicenseTextField from "../components/LicenseTextField";
import NewStatus from "../components/NewStatus";
import { LRS_ENDPOINT, LRS_NOTIFY } from "../config";

function LicenseDetails() {
  const DataModel: { [key: string]: string } = {
    egm_hw_serial: "EGM Serial Number",
    license_validity: "Validity Days",
    expiry_date: "Expiry On (YYYY-MM-DD HH:MM:SS)",
    updated_at: "Updated On",
    license_status: "Status",
    license_code: "Validation Code",
    timeZone: "EGM Time Zone",
    otpCode: "OTP",
    feedback: "Feedback",
    license_type: "License Type",
  };

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [data, setData] = useState<typeof DataModel>();
  const [error, setError] = useState("");
  const [requestID, setRequestID] = useState("");
  const [searchParams] = useSearchParams();
  const [isButtonVisible, setIsButtonVisible] = useState(true);
  const navigate = useNavigate();
  const timeoutRef = useRef<NodeJS.Timeout>();
  const statusTriggerRef = useRef<NodeJS.Timeout>();

  const clearStatusTriggerRef = () => {
    if (statusTriggerRef.current) {
      clearTimeout(statusTriggerRef.current);
      console.log("Timeout cleared.");
    }
  };

  const fetchData = useCallback(async () => {
    if (!data) {
      try {
        const request_id = searchParams.get("reqID");
        console.log("Request ID:", request_id);
        if (request_id) setRequestID(request_id);

        var UUID = searchParams.get("UUID");
        if (!UUID) {
          UUID = searchParams.get("keyVersion");
          if (!UUID) {
            UUID = searchParams.get("toolVersion");
          }
        }

        const reqData = searchParams.get("data");
        const jsonData = {
          request_id: request_id,
          UUID: UUID,
          request_data: reqData,
        };

        const response = await fetch(LRS_ENDPOINT, {
          method: "POST",
          headers: {
            "Content-type": "application/json; charset=UTF-8",
          },
          body: JSON.stringify(jsonData),
        });

        const respJson = await response.json();
        console.log(JSON.stringify(respJson));
        if (respJson.data?.status === "FAILED") {
          setError(respJson.data?.feedback);
          clearStatusTriggerRef();
        } else if (
          respJson.data?.status === "PENDING" &&
          respJson.data?.license_code === null &&
          respJson.data?.license_validity === null
        ) {
          setError(respJson.data?.feedback);
          clearStatusTriggerRef();
        } else if (respJson.data?.status === "NEW") {
          clearStatusTriggerRef();
          statusTriggerRef.current = setInterval(fetchData, 5000);
        } else if (
          respJson.data?.status === "SUCCESS" ||
          respJson.data?.status === "PENDING"
        ) {
          setData(respJson.data);
          setError("");
          clearStatusTriggerRef();
        } else {
          setError("Unexpected response status: " + respJson.data?.status);
          clearStatusTriggerRef();
        }
      } catch (err: any) {
        console.log(err);
        setError(err.toString());
        clearStatusTriggerRef();
      }
    }

    // Clean up on unmount
    return () => {
      clearStatusTriggerRef();
    };
  }, [searchParams, data]);

  useEffect(() => {
    const handleWindowEvents = () => {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        navigate("/timeout", { replace: true });
      }, 60000);
    };

    // handleWindowEvents();
    fetchData();
  }, []);

  const handleGenerateOtp = async () => {
    setIsButtonVisible(false); // Hide the button
    try {
      const response = await fetch(LRS_NOTIFY, {
        method: "POST",
        headers: {
          "Content-type": "application/json; charset=UTF-8",
        },
        body: JSON.stringify({
          request_id: requestID,
        }),
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const respJson = await response.json();
      console.log(respJson);
    } catch (err: any) {
      console.log(err);
      setError(err.toString());
    }
  };

  function createData(name: string, details: string) {
    return { name, details };
  }

  const rows = [
    createData(DataModel.egm_hw_serial, data?.egm_hw_serial || ""),
    createData(DataModel.license_validity, data?.license_validity || ""),
    createData(DataModel.expiry_date, data?.expiry_date || ""),
    createData(DataModel.license_status, data?.license_status || ""),
    createData(DataModel.feedback, data?.feedback || ""),
    createData(DataModel.license_type, data?.license_type || ""),
    // createData(DataModel.updated_at, data?.updated_at || ""),
    // createData(DataModel.timeZone, data?.timeZone),
    // createData(DataModel.otpCode, data?.otpCode),
  ];

  const RenderTable = (index: any, name: string, value: string) => {
    return (
      <Box key={index}>
        <Box>
          <Typography variant="subtitle1" color={"grey"}>
            {name}
          </Typography>
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 500, fontSize: 20 }}>
            {value}
          </Typography>
        </Box>
      </Box>
    );
  };

  const DataContent = () => {
    return (
      <Box px={2}>
        <Box border={"2px solid #e0e0e0"} borderRadius={2} p={2} my={2}>
          <Box display={"flex"} alignItems={"center"} gap={1} pb={1}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              License Details
            </Typography>
            <Tooltip title="License Details">
              <InfoOutlineIcon fontSize="small" />
            </Tooltip>
          </Box>
          <Stack my={1} gap={3}>
            {rows.map(
              (row, index) =>
                row.details && RenderTable(index, row.name, row.details),
            )}
          </Stack>
        </Box>
        <LicenseTextField
          label="License Key"
          helper="Enter the license key in Operator Menu."
          data={data?.license_code || ""}
          focused={isButtonVisible}
          shouldFormatLicenseKey={true} // Enable formatting for license key
        />
        {isButtonVisible ? (
          <Box width={"100%"} my={2}>
            <Button
              variant="contained"
              startIcon={
                <PasswordIcon fontSize="small" sx={{ color: "white" }} />
              }
              fullWidth
              sx={{
                paddingY: 1.5,
                paddingX: 2,
                borderRadius: 2,
                backgroundColor: "#2BB132",
                "&:hover": {
                  backgroundColor: "#388E3C",
                },
              }}
              // size="large"
              onClick={handleGenerateOtp}
            >
              Generate OTP
            </Button>
          </Box>
        ) : (
          <LicenseTextField
            label="Generated OTP"
            helper="Enter the OTP in Operator Menu."
            data={data?.otpCode || ""}
            focused={!isButtonVisible}
            shouldFormatLicenseKey={false} // Disable formatting for OTP
          />
        )}
      </Box>
    );
  };

  const DisplayContent = () => {
    if (error) {
      return <ErrorsDisplay error={error} smallScreen={isSmallScreen} />;
    } else if (data) {
      return <DataContent />;
    } else {
      return <NewStatus />;
    }
  };

  return (
    <Box height={"100%"} width={"100%"} className="displayCenter">
      <Card
        sx={{
          p: 1,
          borderRadius: 3,
          width: isSmallScreen ? "90%" : isMediumScreen ? "60%" : "30%",
          border: "0 solid #3250ff",
          borderWidth: 1.5,
          backgroundColor: "#F0F0F0",
        }}
      >
        <Paper
          className="displayCenter"
          elevation={5}
          sx={{
            borderRadius: 3,
            background: "linear-gradient(45deg, #000000, #3250ff)",
            // background: "linear-gradient(45deg, #3250ff, #000000)",
            // background: " #3250ff",
          }}
        >
          <img src={logo} height={100} alt="LNW" />
        </Paper>
        <Box
          sx={{ animation: "popupAnimation 1s ease forwards" }}
          overflow="auto"
        >
          <DisplayContent />
        </Box>
      </Card>
    </Box>
  );
}

export default LicenseDetails;
